// src/components/team-messages/TeamMessagesSidebar.tsx
import { useState } from 'react';
import { Maximize2, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import * as SheetPrimitive from '@radix-ui/react-dialog';
import { useTeamMessages } from '@/hooks/useTeamMessages';
import { ConversationListPanel } from './ConversationListPanel';
import { ThreadPanel } from './ThreadPanel';
import type { TeamConversation } from '@/types/teamMessages';

interface TeamMessagesSidebarProps {
  open: boolean;
  onClose: () => void;
}

export function TeamMessagesSidebar({ open, onClose }: TeamMessagesSidebarProps) {
  const navigate = useNavigate();
  const [activeConversation, setActiveConversation] = useState<TeamConversation | null>(null);

  const hook = useTeamMessages();
  const { currentUserId } = hook;

  const handleSelectConversation = (conv: TeamConversation) => {
    setActiveConversation(conv);
    hook.selectConversation(conv.id);
  };

  const handleBack = () => {
    setActiveConversation(null);
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      onClose();
      setActiveConversation(null);
    }
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetPortal>
        {/* Transparent overlay — clicking outside closes, but no dim */}
        <SheetOverlay className="bg-transparent" />

        {/* Custom content — no built-in padding or close button */}
        <SheetPrimitive.Content
          className="fixed inset-y-0 right-0 z-50 h-full w-[480px] bg-[#0a0a0a] border-l border-[#2e2e2e] flex flex-col data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right data-[state=closed]:duration-300 data-[state=open]:duration-500"
        >
          {/* Required for accessibility */}
          <SheetTitle className="sr-only">Team Messages</SheetTitle>
          <SheetDescription className="sr-only">Internal team messaging panel</SheetDescription>

          {/* Panel header */}
          <div className="flex items-center gap-2 px-4 py-3.5 border-b border-[#2e2e2e] shrink-0">
            <span className="flex-1 text-base font-semibold tracking-tight text-white">
              {activeConversation ? null : 'Messages'}
            </span>
            <button
              onClick={() => navigate('/team-messages')}
              className="p-1.5 rounded-md hover:bg-[#1e1e1e] transition-colors text-[#5a5a5a] hover:text-white"
              title="Open full messaging view"
            >
              <Maximize2 className="h-4 w-4" />
            </button>
            <button
              onClick={onClose}
              className="p-1.5 rounded-md hover:bg-[#1e1e1e] transition-colors text-[#5a5a5a] hover:text-white"
              title="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Screens */}
          <div className="flex-1 min-h-0">
            {activeConversation === null ? (
              <ConversationListPanel
                conversations={hook.conversations}
                loadingConversations={hook.loadingConversations}
                currentUserId={currentUserId}
                orgMembers={hook.orgMembers}
                updatesChannelId={hook.updatesChannelId}
                onSelectConversation={handleSelectConversation}
              />
            ) : (
              <ThreadPanel
                conversation={activeConversation}
                messages={hook.messages}
                loadingMessages={hook.loadingMessages}
                updatesChannelId={hook.updatesChannelId}
                currentUserId={currentUserId}
                orgMembers={hook.orgMembers}
                onSend={hook.handleSend}
                onBack={handleBack}
              />
            )}
          </div>
        </SheetPrimitive.Content>
      </SheetPortal>
    </Sheet>
  );
}
